// Standalone copy of @bitespeed/shared/frame-guard. This app builds with plain
// react-scripts (no CRACO), which can't import the raw-TS shared module from
// outside src/. Keep ALLOWED_PARENT_ORIGINS in sync with the shared module.
//
// `frame-ancestors` (server-side) blocks untrusted sites from embedding us, but
// it does NOT stop someone from opening this URL directly in a tab. This guard
// covers that gap: it only returns true when we are genuinely framed by an
// allowed parent. Must run client-side (relies on `window`).
export const ALLOWED_PARENT_ORIGINS = [
  'https://chat.bitespeed.co',
  'https://csdbtest.bitespeed.co',
];

/**
 * @param {boolean} isDevelopment Pass `process.env.NODE_ENV !== 'production'`.
 *   When true the guard is bypassed so the app opens directly in local dev.
 */
export function isAllowedFrame(isDevelopment = false) {
  if (isDevelopment) {
    return true;
  }

  // Chatwoot mobile app loads dashboard apps as the top-level document of a
  // react-native-webview (no iframe), so the origin checks below can never
  // pass. `window.ReactNativeWebView` exists only inside that WebView and is
  // absent in a normal browser tab, so it's a safe "inside the app" signal.
  if (window.ReactNativeWebView) {
    return true;
  }

  // Top-level navigation (direct open / new tab): window is its own top.
  if (window.self === window.top) {
    return false;
  }

  // Preferred: ancestorOrigins can't be spoofed by the parent (Chromium, Safari).
  const ancestors = window.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0) {
    return ALLOWED_PARENT_ORIGINS.includes(ancestors[0]);
  }

  // Fallback (Firefox lacks ancestorOrigins): use the referrer origin.
  if (document.referrer) {
    try {
      const referrerOrigin = new URL(document.referrer).origin;
      return ALLOWED_PARENT_ORIGINS.includes(referrerOrigin);
    } catch (e) {
      return false;
    }
  }

  // Framed, but parent origin can't be verified — fail closed.
  return false;
}
